import React from 'react';
import {useAuth} from '../util/auth';
import {useSelector} from 'react-redux';
import {makeGetCaseByStatus, selectCaseById} from '../features/casesSlice';
import {CaseStatuses} from '../constants';
import {useAddToCalendar} from './AddCaseToCalendarLink';
import {shouldShowJoinCaseButton} from '../util/cases';
import {formatDate, getTimeToStart} from '../util/format';
import {selectTimezone} from '../features/timezoneSlice';
import Link from 'next/link';
import {Button} from '@/components/ui/button';
import {ArrowUpRight, CalendarPlus} from 'lucide-react';

const myScheduledCasesSelector = makeGetCaseByStatus({
    selectedStatus: [CaseStatuses.SCHEDULED, CaseStatuses.TENTATIVE],
    sortKey: 'scheduled_to',
    userParticipantFilter: true,
});

const UserCasesList = ({showCount = 5}) => {
    const auth = useAuth();
    const myCases = useSelector((state) => {
        const now = Date.now() / 1000;
        return myScheduledCasesSelector(state).filter(it => {
            const diff = it.scheduled_to - now;
            const hourInMilliseconds = 3600000;

            if (diff > 0) {
                return true;
            }

            return Math.abs(diff) < 2 * hourInMilliseconds;
        })
    });

    if (!auth.isAuthenticated) {
        return null;
    }

    if (!myCases || !myCases.length) {
        return (
            <div className="flex flex-col items-start gap-2">
                <span className="font-semibold text-sm">
                    You have not registered for any session yet.
                </span>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {(myCases || []).slice(0, showCount).map(it => {
                return <UserCasesListItem key={it.id} caseId={it.id} />
            })}
        </div>
    )
};

const UserCasesListItem = ({caseId}: {
    caseId: string,
}) => {
    const currentCase = useSelector((state) => selectCaseById(state, caseId));
    const timezone = useSelector(selectTimezone);
    const handleAddToCalendar = useAddToCalendar(caseId);

    if (!currentCase) {
        return null;
    }

    const isPlaceholder = currentCase.state === CaseStatuses.TENTATIVE;
    const showJoinButton = shouldShowJoinCaseButton(currentCase);
    const timeToStart = currentCase.scheduled_to - Date.now() / 1000;
    const isStartingSoon = timeToStart < 600;

    return (
        <div className="flex flex-col items-start gap-2">
            {isStartingSoon && (
                <p className="text-xs text-teal-200">
                    {getTimeToStart(timeToStart)}
                </p>
            )}
            {!isStartingSoon && (
                <p className="text-xs text-muted-foreground">
                    {formatDate({ts: currentCase.scheduled_to, format: 'long', tz: timezone})}
                </p>
            )}

            {isPlaceholder ? (
                <span className="font-semibold text-sm">
                    {currentCase.title}
                </span>
            ) : (
                <Link href={!isPlaceholder ? `/case/${caseId}` : '#'} passHref>
                    <a className="font-semibold text-sm">
                        {currentCase.title}
                    </a>
                </Link>
            )}
            {!showJoinButton ? (
                <Button variant="link" onClick={handleAddToCalendar} className="text-sm">
                    <CalendarPlus className="w-4 h-4" /> Add to calendar
                </Button>
            ) : (
                <Button variant="link" className="text-sm" asChild>
                    <a href={currentCase.zoom_us_link}>
                        <ArrowUpRight className="w-4 h-4" /> Join the session
                    </a>
                </Button>
            )}
        </div>
    )
}

export default UserCasesList;
